<template lang="pug">
.toolbox
  .coordBox
    .coordInfo(v-html="coordInfo")
    button-menu
        menu-item(:label=`$t('sexadecimal')`, type="radio", value="SEXADECIMAL", v-model="module.coordFormat")
        menu-item(:label=`$t('decimal')`, type="radio", value="DECIMAL", v-model="module.coordFormat")
        menu-separator
        menu-item(:label=`$t('copy_to_clipboard')`, @click="copyCoordToClipboard", :sync="true")
        menu-separator
        menu-item(:label=`$t('ruler')`, v-model="Store.modules.rulerModule.persistent.show")
  .zoomBox
    transition(name="fade")
      .ruler(v-if="tic", style="display: flex;")
        | &vert;&larr; <span v-html="ticHtml"/> &rarr;&vert;
        color-picker(v-model="Store.modules.rulerModule.persistent.color")
    button(@click="zoom(-1)")
      i.material-icons zoom_in
    button(@click="zoom(+1)")
      i.material-icons zoom_out
</template>


<script lang="ts">
import { Vue, Component, Prop } from "vue-property-decorator";
import { CoordIndicatorModule } from "@/modules/CoordIndicatorModule";
import { dom } from "@/utils/dom";
import { Frame, store } from "@/store";
import { EquatorialCoord, rad2amin, rad2deg } from "@hscmap/angle";
import { angle60html } from "@/utils/angle";
import { sprintf } from 'sprintf-js';
import { CoordFormat } from '../../utils/coord_format';
import { ITic } from '../RulerModule';
import { queryParams } from '../../utils/query_params';

@Component
export default class extends Vue {
  @Prop({ required: true })
  module!: CoordIndicatorModule

  @Prop({ required: true })
  frame!: Frame

  get coordInfo() {
    const { a, d, fovy } = this.frame.view
    const coord = EquatorialCoord.fromRad(a, d)
    switch (this.module.coordFormat) {
      case CoordFormat.SEXADECIMAL: {
        const { a: a60, d: d60 } = coord.toString()
        const angle = angle60html(2 * fovy)
        return `&alpha;=${a60} &delta;=${d60} &angle;=${angle}`
      }
      case CoordFormat.DECIMAL: {
        const { a: aAng, d: dAng } = coord
        return sprintf(`&alpha;=%08.6f&deg; &delta;=%08.6f&deg; &angle;=%08.6f`, aAng.deg, dAng.deg, rad2deg(2 * fovy))
      }
    }
  }

  get ticHtml() {
    const tic = this.tic as ITic
    return tic.name.replace('deg', '&deg;').replace('arcsec', '&Prime;').replace('arcmin', '&prime;')
  }

  get tic() {
    const module = store.modules.rulerModule
    const tic = this.frame.modules.rulerModule.tic
    return module.persistent.show && tic
  }

  copyCoordToClipboard() {
    dom.pbcopy(this.coordInfo, true)
  }

  async zoom(a: number) {
    const z = Math.pow(2, a)
    const globe = store.globe(this.frame)
    const vf = globe.viewFactory
    await vf.jumpTo({ fovy: vf.fovy * z })
    vf.cage()
  }
}
</script>


<style lang="scss" scoped>
.coordBox {
  position: fixed;
  right: 0;
  bottom: 0;
  background-color: rgba(63, 63, 63, 0.75);
  border-radius: 4pt;
  padding: 0.25em 0.5em;
  font-family: monospace;
  margin: 1px;
  display: inline-block;
  color: white;
}

.coordInfo {
  font-family: monospace;
  display: inline-block;
  margin-right: 1em;
  color: #0f0;
}

.zoomBox {
  position: fixed;
  display: flex;
  justify-content: flex-end;
  top: 0;
  right: 0;
  button {
    margin: 1px;
  }
}

.ruler {
  color: #aaa;
  align-self: center;
  background-color: rgba(63, 63, 63, 0.75);
  border-radius: 4pt;
  padding: 0.25em 0.5em;
  font-family: monospace;
  margin: 1px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<i18n>
en:
  sexadecimal: Sexadecimal
  decimal: Decimal
  copy_to_clipboard: Copy to Clipboard
  ruler: Ruler
ja:
  sexadecimal: 60進
  decimal: 10進
  copy_to_clipboard: コピー
  ruler: ルーラー
</i18n>